<template>
  <div :key="colorOn + colorOff" class="svg-container" :style="statusColor">
    <EqDaSvg />
  </div>
</template>

<script setup lang="ts">
/**
 * De adressen in het bereik 936 - 999 zijn adressen met gedefinieerde functies in het apparaat.
 * Maar deze adressen worden OOK gebruikt om "Device Status LEDs" aan te sturen op de dashboard
 * pagina.
 *
 * Op het apparaat geven 936 - 939 aan wat voor PWM [Pulse Width Modulation]-signaal naar
 * verbonden valves wordt verstuurd. Dit PWM-signaal wordt gelijk gebruikt om de bijbehorende
 * LED op het apparaat aan te sturen als indicator voor valve gebruik.
 * Om dit in de GUI te reflecteren worden adressen 936 - 939 uitgelezen om deze waardes weer te
 * kunnen geven bij "Device Status LEDs".
 *
 * Adressen 990 - 999 zijn feedback adressen die aangeven wat de huidige status van de middelste
 * LED is. Deze adressen worden vervolgens uitgelezen door de GUI om de status weer te kunnen
 * geven bij "Device Status LEDs".
 */
import { computed, ref, onMounted, onUnmounted } from "vue";
import EqDaSvg from "@/assets/eq_da_gui.svg";
import { useByteUtils } from "@/composables/useByteUtils";
import { useUtils } from "@/composables/useUtils";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { TimerId } from "@/types/TimerId";

const { modbusAddress } = useModbusAddress();
const modbusStore = useModbusStore();
const { clamp } = useUtils();
const { uint16_to_bytes, bytes_to_float, two_uint16_to_uint32 } =
  useByteUtils();

const colorGrey = "#333333";

const isModbusAvailable = computed<boolean>(
  () => modbusStore.storeInit && modbusStore.portState === PortState.connected,
);

const timerOn = computed(() => {
  if (!isModbusAvailable.value) {
    return 0;
  }
  return two_uint16_to_uint32(
    modbusStore.modbusValues[modbusAddress.ledOnTime],
    modbusStore.modbusValues[modbusAddress.ledOnTime + 1],
  );
});

const timerOff = computed(() => {
  if (!isModbusAvailable.value) {
    return 0;
  }
  return two_uint16_to_uint32(
    modbusStore.modbusValues[modbusAddress.ledOffTime],
    modbusStore.modbusValues[modbusAddress.ledOffTime + 1],
  );
});

const colorOn = computed(() => {
  if (!isModbusAvailable.value) {
    return colorGrey;
  }
  const rr =
    modbusStore.modbusValues[modbusAddress.ledOnRed] === 0 ? "00" : "ff";
  const gg =
    modbusStore.modbusValues[modbusAddress.ledOnGreen] === 0 ? "00" : "ff";
  const bb =
    modbusStore.modbusValues[modbusAddress.ledOnBlue] === 0 ? "00" : "ff";
  return `#${rr}${gg}${bb}`;
});

const colorOff = computed(() => {
  if (!isModbusAvailable.value) {
    return colorGrey;
  }
  const rr =
    modbusStore.modbusValues[modbusAddress.ledOffRed] === 0 ? "00" : "ff";
  const gg =
    modbusStore.modbusValues[modbusAddress.ledOffGreen] === 0 ? "00" : "ff";
  const bb =
    modbusStore.modbusValues[modbusAddress.ledOffBlue] === 0 ? "00" : "ff";
  return `#${rr}${gg}${bb}`;
});

const colorIn = computed(() => {
  if (!isModbusAvailable.value) {
    return colorGrey;
  }
  const bytes1 = uint16_to_bytes(
    modbusStore.modbusValues[modbusAddress.valveInDuty],
  );
  const bytes2 = uint16_to_bytes(
    modbusStore.modbusValues[modbusAddress.valveInDuty + 1],
  );
  const byteValue = bytes_to_float([
    bytes1[0],
    bytes1[1],
    bytes2[0],
    bytes2[1],
  ]);
  const value = Math.round((clamp(byteValue, 0, 100) * 255) / 100).toString(16);
  return `#00${value}00`;
});

const colorOut = computed(() => {
  if (!isModbusAvailable.value) {
    return colorGrey;
  }
  const bytes1 = uint16_to_bytes(
    modbusStore.modbusValues[modbusAddress.valveOutDuty],
  );
  const bytes2 = uint16_to_bytes(
    modbusStore.modbusValues[modbusAddress.valveOutDuty + 1],
  );
  const byteValue = bytes_to_float([
    bytes1[0],
    bytes1[1],
    bytes2[0],
    bytes2[1],
  ]);
  const value = Math.round((clamp(byteValue, 0, 100) * 255) / 100).toString(16);
  return `#00${value}00`;
});

const statusColor = computed(() => {
  const percent = Math.round(
    (timerOn.value * 99) / (timerOn.value + timerOff.value),
  );
  return (
    Array(101)
      .fill(0)
      .map(
        (_, i) =>
          `--color-status-led-${i}: ${
            i > 99 || i <= percent ? colorOn.value : colorOff.value
          };`,
      )
      .join("\n") +
    `
    --animation-duration: ${timerOn.value + timerOff.value}ms;
    --in-led-color: ${colorIn.value};
    --out-led-color: ${colorOut.value};
    `
  );
});

const modbusReqTimer = ref<TimerId>(0);

onMounted(async () => {
  modbusReqTimer.value = setInterval(async () => {
    if (isModbusAvailable.value) {
      modbusStore.modbusRead(
        modbusStore.deviceAddress,
        modbusAddress.ledOnTime,
        10,
      );
      modbusStore.modbusRead(
        modbusStore.deviceAddress,
        modbusAddress.valveInDuty,
        4,
      );
    }
  }, 600);
});

onUnmounted(() => {
  clearInterval(modbusReqTimer.value);
});
</script>

<style scoped lang="scss">
@keyframes blink {
  @for $i from 0 through 101 {
    #{$i + "%"} {
      --status-led-color: var(--color-status-led-#{$i});
    }
  }
}
.svg-container {
  animation: blink var(--animation-duration) infinite;
  height: 100%;
  width: 100%;
}
</style>
